<template>
	<div class="inline-flex flex-col items-center">
		<svg :width="width" :height="Math.round(width * 2.1)" viewBox="0 0 200 420" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Mapa corporal com regiões marcadas na matriz de risco">
			<!-- Convenção anatômica: lado DIREITO do paciente à esquerda do observador -->
			<text x="14" y="18" text-anchor="middle" font-size="11" font-weight="bold" fill="#94a3b8">D</text>
			<text x="186" y="18" text-anchor="middle" font-size="11" font-weight="bold" fill="#94a3b8">E</text>

			<g fill="#16324c" stroke="none">
				<circle cx="100" cy="27" r="16" />
				<rect x="92" y="39" width="16" height="16" />
				<path d="M63 55 Q100 43 137 55 L131 118 Q127 142 129 162 L133 196 Q100 208 67 196 L71 162 Q73 142 69 118 Z" />
				<circle cx="43" cy="200" r="7.5" />
				<circle cx="157" cy="200" r="7.5" />
				<ellipse cx="70" cy="388" rx="13" ry="8" />
				<ellipse cx="130" cy="388" rx="13" ry="8" />
			</g>
			<g fill="none" stroke="#16324c" stroke-linecap="round" stroke-linejoin="round">
				<path d="M64 60 L52 128 L44 192" stroke-width="13" />
				<path d="M136 60 L148 128 L156 192" stroke-width="13" />
				<path d="M84 198 L79 290" stroke-width="24" />
				<path d="M116 198 L121 290" stroke-width="24" />
				<path d="M79 290 L74 372" stroke-width="15" />
				<path d="M121 290 L126 372" stroke-width="15" />
			</g>

			<g v-for="point in points" :key="point.label">
				<path v-if="point.spine" :d="diamondPath(point)" :fill="point.color" stroke="#ffffff" stroke-width="2">
					<title>{{ point.label }} — {{ point.band }}</title>
				</path>
				<circle v-else-if="point.posterior" :cx="point.x" :cy="point.y" r="6.5" fill="#ffffff" :stroke="point.color" stroke-width="3.5">
					<title>{{ point.label }} — {{ point.band }}</title>
				</circle>
				<circle v-else :cx="point.x" :cy="point.y" r="7" :fill="point.color" stroke="#ffffff" stroke-width="2">
					<title>{{ point.label }} — {{ point.band }}</title>
				</circle>
			</g>
		</svg>

		<div v-if="showLegend" class="mt-2 flex max-w-[230px] flex-wrap justify-center gap-x-3 gap-y-1">
			<div v-for="(color, band) in bands" :key="band" class="flex items-center gap-1">
				<span class="inline-block h-3 w-3 rounded-full" :style="{ backgroundColor: color }"></span>
				<span class="text-xs font-medium text-slate-600">{{ band }}</span>
			</div>
		</div>
		<div v-if="showLegend && hasSpecialMarkers" class="mt-1 text-[10px] text-slate-500">◆ coluna &nbsp;·&nbsp; ◯ face posterior</div>
	</div>
</template>

<script>
import { computed } from 'vue';
import { classifyRiskBand, riskColor, RISK_BAND_COLORS } from '../utils/risk';

// Coordenadas (viewBox 0 0 200 420) por label canônico da matriz (Matrix.vue).
// Vista frontal: lado direito do paciente fica à esquerda do observador.
const BODY_POINTS = {
	'Cabeça': { x: 100, y: 27 },
	'Pescoço': { x: 100, y: 48 },
	'Coluna cervical': { x: 100, y: 64, spine: true },
	'Ombro direito': { x: 68, y: 63 },
	'Ombro esquerdo': { x: 132, y: 63 },
	'Coluna torácica': { x: 100, y: 105, spine: true },
	'Cotovelo direito': { x: 52, y: 128 },
	'Cotovelo esquerdo': { x: 148, y: 128 },
	'Coluna lombar': { x: 100, y: 150, spine: true },
	'Punho e mão direita': { x: 44, y: 194 },
	'Punho e mão esquerda': { x: 156, y: 194 },
	'Quadril direito': { x: 78, y: 188 },
	'Quadril esquerdo': { x: 122, y: 188 },
	'Adutor direito': { x: 91, y: 220 },
	'Adutor esquerdo': { x: 109, y: 220 },
	'Coxa anterior direita': { x: 82, y: 240 },
	'Coxa anterior esquerda': { x: 118, y: 240 },
	'Coxa posterior direita': { x: 81, y: 266, posterior: true },
	'Coxa posterior esquerda': { x: 119, y: 266, posterior: true },
	'Joelho direito': { x: 79, y: 292 },
	'Joelho esquerdo': { x: 121, y: 292 },
	'Perna direita': { x: 77, y: 330 },
	'Perna esquerda': { x: 123, y: 330 },
	'Tornozelo direito': { x: 74, y: 368 },
	'Tornozelo esquerdo': { x: 126, y: 368 },
	'Pé direito': { x: 69, y: 390 },
	'Pé esquerdo': { x: 131, y: 390 }
};

export default {
	props: {
		data: {
			type: Array,
			default: () => []
		},
		showLegend: {
			type: Boolean,
			default: true
		},
		width: {
			type: Number,
			default: 190
		}
	},
	setup(props) {
		const points = computed(() =>
			(props.data || [])
				.map((item) => {
					const label = typeof item === 'string' ? item : item?.label;
					const coords = BODY_POINTS[(label || '').trim()];
					if (!coords) return null;

					const assessed = item?.probability || item?.impact;
					return {
						...coords,
						label,
						band: assessed ? classifyRiskBand(item) : 'Não avaliado',
						color: assessed ? riskColor(item) : '#94a3b8'
					};
				})
				.filter(Boolean)
		);

		const hasSpecialMarkers = computed(() => points.value.some((point) => point.spine || point.posterior));

		function diamondPath(point) {
			const r = 8;
			return `M ${point.x} ${point.y - r} L ${point.x + r} ${point.y} L ${point.x} ${point.y + r} L ${point.x - r} ${point.y} Z`;
		}

		return {
			points,
			hasSpecialMarkers,
			diamondPath,
			bands: RISK_BAND_COLORS
		};
	}
};
</script>
